import { ACCOUNT_ALREADY_EXISTS_ERROR_CODE } from '@commutifi/constants/Errors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLongArrowRight } from '@commutifi-fe/custom-icons/regular'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { HttpStatusCode } from '@commutifi-fe/constants'
import { saveAuthenticated } from 'api/index'
import { PlanFrequency } from 'api/modules/commuterPlans'
import { usePatchSurveyAccount } from 'api/modules/survey'
import { FormattedMessage, useIntl } from 'locales/index'
import { isFinite } from 'lodash'
import omit from 'lodash/omit'
import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { MetricsCard } from '@commutifi-fe/commutifi-specific/Presentation/MetricsLocalizer'
import {
  Alert,
  Button as AppButton,
  CommuteScore,
  GenericError,
  CommutifiLogo,
  MarkdownWrapper,
  Typography
} from '@commutifi-fe/ui'
import { useModal } from '@commutifi-fe/hooks'
import { useNotifications } from '@commutifi-fe/commutifi-specific/hooks'
import { ProcessSurveyResponse } from 'shared/hooks/useProcessSurvey'
import { useScrollToTop } from 'shared/hooks/useScrollToTop'
import { RegisterFormPayload } from 'shared/interfaces/Account'
import { Score } from 'shared/interfaces/Commute'
import { useConfig } from 'shared/providers'
import { selectors as surveySelectors } from 'store/modules/enterpriseSurvey'
import { resetSurveyAnswers } from 'store/modules/enterpriseSurvey/sagas'
import { renderCommuteCarbonHint, renderCommuteCostHint, renderCommuteDurationHint } from 'utils/helpers/metrics'
import { getCommutifiErrorCode } from 'utils/index'
import SurveyLayout from 'views/Anonymous/views/Onboarding/layout/SurveyLayout'
import { colors, helpEmail } from 'constants/global'
import { registerScreenPathRegexp } from '../SurveyLandingPage'
import CreateAccountModal from './components/CreateAccountModal'
import styles from './styles.module.scss'

const { Title, Text, Link } = Typography

interface SurveySuccessScreenProps extends RouteComponentProps<any, any, ISurveySuccessLocationState> {
  errorCode?: number
  title: string
  logo: string | React.ReactNode
  markdownContent: string
  score?: number
  metrics?: Omit<Score, 'id' | 'global'>
  commuteFrequency?: number
  isAnonymous?: boolean
  isDirectOnboarding?: boolean
  surveyId?: string
}

interface ISurveySuccessLocationState {
  origin: string
  errorCode?: number
  data?: ProcessSurveyResponse & { commuteFrequency?: number }
}

function SurveySuccessScreen({
  errorCode,
  logo,
  title,
  markdownContent,
  score,
  metrics,
  commuteFrequency,
  isAnonymous,
  isDirectOnboarding,
  surveyId,
  history
}: SurveySuccessScreenProps) {
  useScrollToTop()
  const intl = useIntl()
  const { locale, currency } = useConfig()
  const [duplicatedAccount, setDuplicatedAccount] = React.useState<'error.duplicate' | null>()
  const { openModal, isOpen, closeModal } = useModal({ onClose: () => setDuplicatedAccount(null) })
  const commutifiNotification = useNotifications()

  const dispatch = useDispatch()
  const dispatchResetSurveyAnswers = React.useCallback(() => dispatch(resetSurveyAnswers()), [dispatch])
  React.useEffect(() => {
    dispatchResetSurveyAnswers()
  }, [dispatchResetSurveyAnswers])

  const logoDom = logo ? typeof logo === 'string' ? <img src={logo} alt="Survey success" /> : logo : null

  const { isPending: isLoading, mutateAsync: patchAccount } = usePatchSurveyAccount(surveyId)
  const handleCreateAccount = async (account: RegisterFormPayload) => {
    try {
      const res = await patchAccount(omit(account, 'agreement'))

      if (!res?.accountId) {
        throw new Error("Patch account didn't return accountId")
      }

      // Set the accountId in the storage (case direct onboarding)
      saveAuthenticated(res?.accountId, { stayLoggedIn: true })

      closeModal()
      history.push(`/accounts/${res.accountId}`)
    } catch (e) {
      const commutifiErrorCode = getCommutifiErrorCode(e)
      if (commutifiErrorCode === ACCOUNT_ALREADY_EXISTS_ERROR_CODE) {
        setDuplicatedAccount('error.duplicate')
      } else {
        closeModal()
        commutifiNotification.error({
          description: intl.formatMessage({ id: 'onboarding.anonymous.createAccount.error' })
        })
      }
    }
  }

  return (
    <div className={styles['success-container']}>
      <div className={styles['success-content']}>
        <div className={styles['success-header']}>
          <CommutifiLogo fill={colors.primary400} />
          <div className={styles['success-title']}>
            <Title level={1} variant="h3" className="u-color--neutral-600">
              {title || intl.formatMessage({ id: 'outro.defaultTitle' })}
            </Title>
          </div>
        </div>

        {errorCode && errorCode >= 0 ? (
          <Alert
            type="error"
            message={
              <span>
                <FontAwesomeIcon icon={faExclamationCircle} className="u-margin-right--small" />
                <FormattedMessage id="error.onboarding.title" />
              </span>
            }
            description={
              <FormattedMessage
                id="error.onboarding.details"
                values={{
                  helpEmail: (
                    <Link href={`mailto:${helpEmail}`} target="_blank">
                      {helpEmail}
                    </Link>
                  )
                }}
              />
            }
          />
        ) : null}
        {isAnonymous && isFinite(score) ? (
          <>
            <Text type="secondary" style={{ textAlign: 'center' }}>
              <FormattedMessage id="onboarding.success.score.description" />
            </Text>
            <div className="u-margin-bottom--medium u-margin-top--medium-2" style={{ width: '100%' }}>
              <div className="u-margin-vertical--medium-2">
                <CommuteScore score={score} />
              </div>
              {metrics ? (
                <MetricsCard
                  locale={locale}
                  rowProps={{ className: 'u-flex-1 u-margin-bottom--large-2 ' }}
                  colProps={{ xs: 24, md: 8 }}
                >
                  <MetricsCard.Cost value={metrics.cost} frequency={PlanFrequency.DAILY}>
                    {renderCommuteCostHint(intl, metrics.cost, currency)}
                  </MetricsCard.Cost>
                  <MetricsCard.Time value={metrics.time} frequency={PlanFrequency.DAILY}>
                    {renderCommuteDurationHint(intl, metrics.time, commuteFrequency)}
                  </MetricsCard.Time>
                  <MetricsCard.Carbon value={metrics.carbon} frequency={PlanFrequency.DAILY}>
                    {renderCommuteCarbonHint(intl, metrics.carbon)}
                  </MetricsCard.Carbon>
                </MetricsCard>
              ) : null}
            </div>
            {isDirectOnboarding ? (
              <>
                <AppButton
                  type="primary"
                  onClick={openModal}
                  loading={isLoading}
                  className={AppButton.Classes.floatOnMobile}
                >
                  <FormattedMessage id="onboarding.success.button.createAccount" />
                  <FontAwesomeIcon size="lg" icon={faLongArrowRight} className="u-margin-left--small" />
                </AppButton>
                <CreateAccountModal
                  open={isOpen}
                  confirmLoading={isLoading}
                  onCancel={closeModal}
                  onCreate={handleCreateAccount}
                  title={`${intl.formatMessage({
                    id: 'components.registerScreen.directOnboarding.subtitle.1'
                  })}${intl.formatMessage({ id: 'components.registerScreen.directOnboarding.subtitle.2' })}`}
                >
                  {duplicatedAccount ? (
                    <Alert closable type="error" message={<FormattedMessage id={duplicatedAccount} />} />
                  ) : null}
                </CreateAccountModal>
              </>
            ) : null}
          </>
        ) : null}

        <div className="u-flex-justify-center">
          <div className={styles['success-content']}>
            {logoDom}
            <MarkdownWrapper source={markdownContent || intl.formatMessage({ id: 'outro.defaultContent' })} />
          </div>
        </div>
      </div>
    </div>
  )
}

function SurveySuccessScreenContainer({ errorCode, ...props }: SurveySuccessScreenProps) {
  const { history, location, isAnonymous } = props
  const handleGoBack = () => {
    history.goBack()
  }

  if (
    !isAnonymous &&
    (!location.state || (location.state.origin && !registerScreenPathRegexp.test(location.state.origin)))
  ) {
    return (
      <GenericError
        type="page"
        error={{ status: HttpStatusCode.Forbidden }}
        actions={[
          <AppButton key="page-error-actions--goBack" onClick={handleGoBack}>
            <FormattedMessage id="label.goBack" />
          </AppButton>
        ]}
      />
    )
  }

  return (
    <SurveyLayout>
      <SurveySuccessScreen
        isAnonymous={isAnonymous}
        errorCode={location.state?.errorCode || errorCode}
        score={location.state?.data?.scores?.global}
        metrics={{
          cost: location.state?.data?.metrics?.cost.perDay ?? 0,
          carbon: location.state?.data?.metrics?.carbon.perDay ?? 0,
          time: location.state?.data?.metrics?.time.perDay ?? 0
        }}
        commuteFrequency={location.state?.data?.commuteFrequency}
        {...props}
      />
    </SurveyLayout>
  )
}

// We should define better types but for now the survey module is in js
const mapStateToProps = (state: any) => ({
  title: surveySelectors.getSuccessHeader(state),
  markdownContent: surveySelectors.getSuccessContent(state),
  logo: surveySelectors.getSurveyLogo(state),
  isAnonymous: surveySelectors.getIsAnonymous(state),
  isDirectOnboarding: surveySelectors.getIsDirectOnboarding(state),
  surveyId: surveySelectors.getSurveyId(state)
})

export default connect(mapStateToProps, null)(SurveySuccessScreenContainer)
